import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import { useDownloadFileMutation } from '@/hooks/useDownloadFileMutation';
import InfoCircle from '@/images/circles/InfoCirclePrimary.svg';
import Code from '@/images/Code.svg';
import Copy from '@/images/Copy.svg';
import Download from '@/images/Download.svg';
import Share from '@/images/Share.svg';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Dialog } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useCheckCampaignGif } from '../../api';

const ItemContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
`;

const ItemContainerSpread = styled(ItemContainer)`
    justify-content: space-between;
`;

const ThumbnailItemContainer = styled(ItemContainerSpread)`
    padding-bottom: 16px;
    border-bottom: 1px solid ${theme.colors.black[300]};
`;

const InfoText = styled.div`
    display: inline;
    color: ${({ theme }) => theme.colors.secondary.main};
    font-family: 'Manrope';
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
`;

const AssetsDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        width: 746px;
        padding: 24px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
        max-width: none;
    }
`;

const CloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};
    margin-right: -12px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TitleText = styled.h2`
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.4px;
    margin: 0;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const StyledButton = styled(Button)`
    display: flex;
    gap: 6px;
    align-items: center;
    width: fit-content;
    height: 44px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    background: #fff;
    color: ${theme.colors.black[800]};
    text-transform: none;
    font-weight: 600;
    font-size: 16px;
`;

const DoneButton = styled(StyledButton)`
    margin-left: auto;
`;

const ItemContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`;

const StyledImage = styled.img`
    border-radius: 16px;
    outline: none;
    height: 144px;
    width: 144px;
    object-fit: contain;
`;

const SpinnerContainer = styled.div`
    border: none;
    outline: none;
    height: 144px;
    width: 144px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const ItemTitle = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
    justify-content: flex-start;
    align-items: center;
`;

const ItemSubtitle = styled.span`
    color: ${theme.colors.black[600]};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
`;

const Spacer = styled.div`
    flex-grow: 1;
`;

const ItemCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 11px;
    padding: 12px 16px;
`;

const ItemDivider = styled.hr`
    width: 100%;
    border: none;
    border-bottom: 1px solid ${theme.colors.black[300]};
`;

const ItemCardUpper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ItemCardLower = styled.div`
    display: flex;
    flex-direction: row;
    gap: 12px;
`;

const CodeBox = styled.input`
    background-color: ${theme.colors.black[100]};
    flex-grow: 1;
    padding: 8px;
    border-radius: 8px;
    border: none;
    color: ${theme.colors.black[600]};
    font-family: Manrope;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    outline: none;
`;

const CopyButton = styled(Button)`
    flex-grow: 0;
`;

const AlertContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    justify-content: flex-start;
    align-items: start;
    padding: 16px;
    border: none;
    border-radius: 8px;
    background-color: ${theme.colors.black[100]};
    color: ${theme.colors.black[600]};
    font-family: Manrope;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
`;

const InfoIconContainer = styled.div`
    padding-top: 4px;
`;

const CodeSpan = styled.span`
    display: inline-block;
    border: none;
    border-radius: 4px;
    padding: 2px;
    background-color: ${theme.colors.black[200]};
    color: ${theme.colors.black[500]};
`;

interface Props {
    campaign: ICampaign | null;
    open: boolean;
    onClose: () => void;
}

function filterSensitiveData(campaign: ICampaign): Partial<ICampaign> {
    const { client_key, ...rest } = campaign;
    return { ...rest };
}

export const AssetsModal = ({ open, onClose, campaign }: Props) => {
    const isEmbedEnabled = useFlag('is-embed-campaign-enabled', false);
    const mixpanel = useAnalytics();
    const { mutateAsync: download } = useDownloadFileMutation();
    const { data: isGifExist, isLoading: isGifLoading } = useCheckCampaignGif(campaign);

    const embedCode = useMemo(() => {
        if (!campaign) return '';
        return `<script src="${window.location.origin}/embed-campaign.js" data-campaign-id="${campaign.id}" data-client-key="${campaign.client_key}" data-container-id="" defer></script>`;
    }, [campaign]);

    const campaignUrl = campaign?.result_url || '';

    const downloadThumbnail = useCallback(async () => {
        if (!campaign) return;

        const toDownload = isGifExist ? campaign.gif_url! : campaign.presenter.thumbnail;
        const extension = isGifExist ? '.gif' : '.png';

        mixpanel?.track('campaigns-assets', { event: 'download-gif', campaign: filterSensitiveData(campaign) });
        await download({ dataurl: toDownload, filename: campaign.title, extension });
    }, [campaign, download, isGifExist, mixpanel]);

    const copyLink = useCallback(() => {
        if (!campaign) return;

        navigator.clipboard.writeText(campaign.result_url);

        mixpanel?.track('campaigns-assets', { event: 'copy-url', campaign: filterSensitiveData(campaign) });

        toast.success({
            title: 'URL copied successfully',
            toastId: 'campaign-link-copied-toast',
        });
    }, [campaign, mixpanel]);

    const copySnippet = useCallback(() => {
        if (!campaign) return;

        const text = `
<!-- This script will inject a Campaign iframe into your website. By default, it will add it to the parent element,
    but you can also specify a custom element to inject it into by passing its ID via the data-container-id attribute.
    *NOTE* if you place this snippet in HTML document's <head> section, you must ensure there is a suitable element in 
    the <body> of the document to inject the iframe into. You must then provide the ID of that element to this script
    via the data-container-id attribute. 
-->
${embedCode}
`;
        navigator.clipboard.writeText(text);

        mixpanel?.track('campaigns-assets', { event: 'copy-embed', campaign: filterSensitiveData(campaign) });

        toast.success({
            title: 'Campaign snippet copied successfully',
            toastId: 'campaign-snippet-copied-toast',
        });
    }, [campaign, embedCode, mixpanel]);

    if (!open || !campaign) {
        return null;
    }

    return (
        <AssetsDialog open>
            <Container>
                <Title>
                    <TitleText>Campaign assets</TitleText>
                    <CloseButton ariaLabel="close" onClick={onClose}>
                        <CloseIcon />
                    </CloseButton>
                </Title>

                <ThumbnailItemContainer>
                    <ItemContent>
                        <InfoText>Campaign thumbnail</InfoText>
                        <ItemSubtitle>Use this animated GIF in your campaign email</ItemSubtitle>
                        <Spacer />
                        <Button
                            color="secondary"
                            variant={'outlined'}
                            onClick={downloadThumbnail}
                            disabled={isGifLoading}
                            startIcon={<MaskedIcon src={Download} size="24px" color={theme.colors.black[550]} />}>
                            {isGifExist ? 'Download GIF' : 'Download Thumbnail'}
                        </Button>
                    </ItemContent>
                    <>
                        {isGifExist ? (
                            <StyledImage src={campaign.gif_url} alt="gif" />
                        ) : isGifLoading ? (
                            <SpinnerContainer>
                                <CircularProgress />
                            </SpinnerContainer>
                        ) : (
                            <StyledImage src={campaign.presenter.thumbnail} alt="thumbnail" />
                        )}
                    </>
                </ThumbnailItemContainer>

                {isEmbedEnabled && (
                    <ItemCard>
                        <ItemCardUpper>
                            <ItemTitle>
                                <MaskedIcon src={Code} size="24px" />
                                <InfoText>Embed campaign on your website</InfoText>
                            </ItemTitle>

                            <ItemSubtitle>Copy the code snippet and paste it into your site’s HTML</ItemSubtitle>
                        </ItemCardUpper>

                        <ItemDivider />

                        <ItemCardLower>
                            <CodeBox type={'text'} value={embedCode} onChange={() => {}} />
                            <CopyButton color="secondary" onClick={copySnippet} startIcon={<MaskedIcon src={Copy} size="24px" />}>
                                Copy
                            </CopyButton>
                        </ItemCardLower>
                    </ItemCard>
                )}

                <ItemCard>
                    <ItemCardUpper>
                        <ItemTitle>
                            <MaskedIcon src={Share} size="24px" />
                            <InfoText>Share video page</InfoText>
                        </ItemTitle>

                        <ItemSubtitle>Copy your campaign page URL and share with your audience</ItemSubtitle>
                    </ItemCardUpper>

                    <ItemDivider />

                    <ItemCardLower>
                        <CodeBox type={'text'} value={campaignUrl} onChange={() => {}} />
                        <CopyButton color="secondary" onClick={copyLink} startIcon={<MaskedIcon src={Copy} size="24px" />}>
                            Copy
                        </CopyButton>
                    </ItemCardLower>
                </ItemCard>

                <AlertContainer>
                    <InfoIconContainer>
                        <MaskedIcon src={InfoCircle} size="16px" />
                    </InfoIconContainer>
                    <div>
                        Remember to replace <CodeSpan>{'{{UID}}'}</CodeSpan> with your CRM <b>user ID</b> syntax.
                        <br />
                        For example, if you’re using Hubspot, replace <CodeSpan>{'{{UID}}'}</CodeSpan> with{' '}
                        <CodeSpan>{'{{contact.email}}'}</CodeSpan>.
                    </div>
                </AlertContainer>

                <DoneButton color="secondary" variant="outlined" onClick={onClose}>
                    Done
                </DoneButton>
            </Container>
        </AssetsDialog>
    );
};
